import { useEffect, FC } from 'react';
import {
    SubmitErrorHandler,
    SubmitHandler,
    useForm
} from 'react-hook-form/dist/index.ie11';

import { Text, FieldMessage, Label } from '@blueprint/ui';
import triggerTracking from '@blueprint/tracking';
import { useDispatch } from 'react-redux';
import { setLoggedIn, setUser } from 'reducers/user';

import useRegistration from './../../hooks/useRegistration';
import RegistrationFormData from './../../interfaces/RegistrationFormData';
import { trackAuthSubmit } from '../../tracking';
import { EMAIL_REGEX, PASSWORD_MIN_LENGTH } from '../../validation/rules';
import SocialAuthVariant from '../SignIn/SocialAuthVariant';

import {
    FormSection,
    RequiredIndicator,
    ReassuranceText,
    RegisterButton,
    FieldContainer,
    RequiredContainer
} from '../../AuthModal.styles';
import { validationDecoration } from '@zoopla/shared/forms';
import { RadioGroup, RadioButton } from 'shared/components';
import { SocialAuthProps } from '../../AuthModal.types';
import {
    transformFormData,
    hasNthErrors
} from 'shared/components/AuthModal/utils';
import { InputPassword, TextField } from '@blueprint/text-input';
import SocialAuth from '../SignIn/SocialAuth';
import { User } from 'graphql.generated';
import { AboutYou } from '../../common/AboutYou';

const detailsPasswordFieldGuidance = 'Minimum 8 characters';
const socialCopy = {
    cta: 'or continue with one click',
    facebook: 'Continue with Facebook',
    google: 'Continue with Google'
};

const RegisterForm: FC<
    Pick<
        SocialAuthProps,
        'close' | 'actionAfterAuth' | 'resetActionAfterAuth' | 'onSocialAuth'
    > & { optimizelyVariation?: string | null }
> = ({
    optimizelyVariation,
    close,
    actionAfterAuth,
    resetActionAfterAuth = () => null,
    onSocialAuth
}) => {
    const {
        register,
        handleSubmit,
        errors,
        setError,
        formState,
        reset,
        trigger,
        getValues
    } = useForm<RegistrationFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });

    const { registerUser, generalError, resetGeneralError } = useRegistration(
        setError,
        close
    );
    const reduxDispatch = useDispatch();

    const onRegisterSuccess = async () => {
        reduxDispatch(setLoggedIn(true));
        await actionAfterAuth?.action();
        resetActionAfterAuth();
        close();
    };

    const onSocialAuthSuccess = async (data?: User) => {
        if (data) {
            reduxDispatch(setUser(data));
        }

        if (onSocialAuth) {
            onSocialAuth(data);
        } else {
            close();
        }
    };

    const passedActionAfterAuth = {
        action: onSocialAuthSuccess,
        eventLabel: actionAfterAuth?.eventLabel || 'register_modal',
        segment: actionAfterAuth?.segment
    };

    const resetFormErrors = () => {
        // reset signin form general and field-specific errors
        resetGeneralError();

        const { consent__insights__24, ...currentValues } = getValues();

        reset({
            ...currentValues,
            ...(formState.touched.consent__insights__24 &&
                !formState.errors.consent__insights__24 && {
                    consent__insights__24
                })
        });
    };

    const onFormSubmit: SubmitHandler<RegistrationFormData> = async (
        formData
    ) => {
        const payload = await registerUser(transformFormData(formData));
        if (!payload?.data?.registerUser) {
            return;
        }

        if (payload.data.registerUser.success) {
            onRegisterSuccess();
        }

        trackAuthSubmit({
            payload: payload.data.registerUser,
            formData,
            eventLabel: actionAfterAuth?.eventLabel,
            segment: actionAfterAuth?.segment
        });
    };
    const onFormSubmitError: SubmitErrorHandler<RegistrationFormData> = (
        errors
    ) => {
        if (
            errors.consent__insights__24 &&
            hasNthErrors({ expectedErrors: 1, formErrors: errors })
        ) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'registration',
                eventAction: 'failure_must_select_opt_in',
                eventLabel: actionAfterAuth?.eventLabel
            });
        }
    };

    const onSubmit = handleSubmit(onFormSubmit, onFormSubmitError);

    useEffect(() => {
        // Reset form when Safari / Firefox read the previous page from the Back-Forward Cache
        window.addEventListener('pageshow', function () {
            reset();
        });

        reset();
    }, [reset]);

    return (
        <>
            {optimizelyVariation && optimizelyVariation === 'small_above' ? (
                <SocialAuthVariant
                    optimizelyVariation={optimizelyVariation}
                    copy={socialCopy}
                    type="register"
                    shouldReset={formState.isSubmitting}
                    resetErrors={resetFormErrors}
                    actionAfterAuth={passedActionAfterAuth}
                />
            ) : null}
            <form
                onSubmit={onSubmit}
                noValidate
                data-testid="auth-modal-registration-form"
                method="post"
            >
                <FormSection>
                    {generalError && (
                        <FieldMessage
                            data-testid="general-error"
                            variant="invalid"
                            message="Sorry, something went wrong. Please try again."
                        />
                    )}

                    <FieldContainer>
                        <Label htmlFor="input-email-address" isRequired>
                            Email address
                        </Label>
                        <RequiredContainer>
                            <Text size="small">
                                <RequiredIndicator>*</RequiredIndicator>
                                Required
                            </Text>
                        </RequiredContainer>
                        <TextField
                            id="input-email-address"
                            name="email"
                            type="email"
                            autoComplete="new-email"
                            validationStatus={validationDecoration<RegistrationFormData>(
                                'email',
                                errors,
                                formState.touched
                            )}
                            validationErrorMessage={
                                errors.email &&
                                (errors.email.message ||
                                    'Please enter a valid email address')
                            }
                            ref={register({
                                required: true,
                                pattern: EMAIL_REGEX
                            })}
                            data-testid="email-field"
                        />
                    </FieldContainer>
                    <FieldContainer>
                        <Label isRequired htmlFor="input-password">
                            Create password
                        </Label>
                        <InputPassword
                            id="input-password"
                            name="password"
                            autoComplete="new-password"
                            helpText={detailsPasswordFieldGuidance}
                            validationStatus={validationDecoration<RegistrationFormData>(
                                'password',
                                errors,
                                formState.touched
                            )}
                            validationErrorMessage={
                                errors.password &&
                                (errors.password.message ||
                                    detailsPasswordFieldGuidance)
                            }
                            ref={register({
                                required: true,
                                minLength: PASSWORD_MIN_LENGTH
                            })}
                            data-testid="password-field"
                        />
                    </FieldContainer>
                </FormSection>

                <FormSection>
                    <FieldContainer>
                        <AboutYou dropDownRef={register} />
                    </FieldContainer>
                </FormSection>
                <FormSection>
                    <RadioGroup
                        legend="Email preferences"
                        description="I want to hear about the latest property news, advice and new home developments from Zoopla and its selected partners only."
                        radioGroupName="consent__insights__24"
                        errorMessage={
                            errors.consent__insights__24 &&
                            'Please set your email preferences.'
                        }
                        onClick={() => trigger('consent__insights__24')}
                    >
                        <RadioButton
                            value="true"
                            id="yes"
                            activeTextColor="white"
                            label="Yes, please"
                            ref={register({ required: true })}
                        />
                        <RadioButton
                            value=""
                            id="no"
                            label="No, thanks"
                            activeTextColor="white"
                            activeBackgroundColor="function-error"
                            ref={register({ required: true })}
                        />
                    </RadioGroup>
                </FormSection>
                <ReassuranceText
                    size="secondary"
                    color="grey-slate"
                    isBold
                    data-testid="unsubscribe-text"
                >
                    You can unsubscribe or update your preferences at any time.
                </ReassuranceText>

                <RegisterButton
                    variant="primary"
                    isFullWidth
                    data-testid="register-button"
                    isLoading={formState.isSubmitting}
                >
                    Register
                </RegisterButton>
            </form>
            {!optimizelyVariation || optimizelyVariation === 'control' ? (
                <SocialAuth
                    copy={socialCopy}
                    type="register"
                    shouldReset={formState.isSubmitting}
                    resetErrors={resetFormErrors}
                    actionAfterAuth={passedActionAfterAuth}
                />
            ) : null}
            {optimizelyVariation && optimizelyVariation === 'small_below' ? (
                <SocialAuthVariant
                    optimizelyVariation={optimizelyVariation}
                    copy={socialCopy}
                    type="register"
                    shouldReset={formState.isSubmitting}
                    resetErrors={resetFormErrors}
                    actionAfterAuth={passedActionAfterAuth}
                />
            ) : null}
        </>
    );
};

export default RegisterForm;
